import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Paper, Typography } from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import { useAuth } from './hooks/useAuth';
import { useSubscription } from './contexts/SubscriptionContext';
import SubscriptionPaywall from './components/subscription/SubscriptionPaywall';
import { EASI_DESIGN } from './theme/design-tokens';
import { PageWrapper } from './components/ui/animations';
import BottomDock from './components/BottomDock';
import AgreementGuard from './components/agreements/AgreementGuard';
import {
  LiteracyCurriculumRoute,
  LiteracyProgressRoute,
  LiteracyDashboardRoute,
  LiteracyLessonRoute,
  LiteracyExploreCurriculumRoute,
  LiteracyExploreLessonRoute,
} from './components/literacy/LiteracyRoutes';
import {
  TherapySessionRoute,
  TherapyInboxRoute,
} from './components/therapy/TherapyRoutes';

const d = EASI_DESIGN;

// Lazy load components
const Login = React.lazy(() => import('./components/auth/Login'));
const EASISystem = React.lazy(() => import('./components/easi-system'));
const Assessment = React.lazy(() => import('./components/Assessment'));
const LanguageSampleWorkflow = React.lazy(() => import('./components/language-sample-workflow'));
const SubscriptionPage = React.lazy(() => import('./components/subscription/SubscriptionPage'));
const AdminDashboard = React.lazy(() => import('./components/admin/AdminDashboard'));
const ReportGeneratorWizard = React.lazy(() => import('./components/report-generator/ReportGeneratorWizard'));
const ReportDashboard = React.lazy(() => import('./components/report-generator/ReportDashboard'));
const TermsOfServicePage = React.lazy(() => import('./components/agreements/TermsOfServicePage'));
const BAASelectionPage = React.lazy(() => import('./components/agreements/BAASelectionPage'));
const BAAAcceptancePage = React.lazy(() => import('./components/agreements/BAAAcceptancePage'));
const ActivateAccount = React.lazy(() => import('./components/auth/ActivateAccount'));
const ResetPassword = React.lazy(() => import('./components/auth/ResetPassword'));

const LOADING_MESSAGES = [
  'Preparing your caseload...',
  'Connecting to secure services...',
  'Loading patient records...',
  'Setting up your workspace...',
];

const LOADING_MSG_INTERVAL = 3000;

/**
 * Premium branded loading screen — first impression on every session.
 * White background, full-size logo, green waveform bars, crossfading status text.
 */
function EasiLoadingScreen() {
  const [msgIdx, setMsgIdx] = useState(0);
  const [msgFade, setMsgFade] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setMsgFade(false);
      setTimeout(() => {
        setMsgIdx((prev) => (prev + 1) % LOADING_MESSAGES.length);
        setMsgFade(true);
      }, 260);
    }, LOADING_MSG_INTERVAL);
    return () => clearInterval(timer);
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{ bgcolor: '#FFFFFF' }}
    >
      <style>{`
        @keyframes easiWaveScale {
          0%, 100% { transform: scaleY(0.3); }
          50% { transform: scaleY(1); }
        }
      `}</style>

      {/* Logo — full size, crisp */}
      <Box
        component="img"
        src="/easi-logo.png"
        alt="EASI"
        sx={{
          width: 96,
          height: 96,
          mb: 3,
          display: 'block',
        }}
      />

      {/* Green waveform bars */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, height: 32, marginBottom: 24 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            style={{
              width: 4,
              height: 28,
              borderRadius: 2,
              backgroundColor: '#10B981',
              transformOrigin: 'center',
              animation: `easiWaveScale 1.4s ease-in-out ${i * 0.12}s infinite`,
            }}
          />
        ))}
      </div>

      {/* Crossfading status text */}
      <Typography
        variant="body2"
        sx={{
          color: '#64748B',
          fontWeight: 500,
          fontSize: 14,
          opacity: msgFade ? 1 : 0,
          transition: 'opacity 260ms ease',
          minHeight: 22,
          userSelect: 'none',
        }}
      >
        {LOADING_MESSAGES[msgIdx]}
      </Typography>
    </Box>
  );
}

/**
 * Recoverable error UI shown when /subscriptions/me fails for a reason other
 * than 404 AND we have no last-known-good subscription to fall back to. This
 * is the "subscription unknown due to error" state — never the paywall.
 *
 * Why this exists: the paywall is reserved for confirmed lack of subscription.
 * A network/5xx blip on first fetch must not falsely accuse a paid user.
 */
function SubscriptionUnavailable({ error }: { error: string }) {
  const { signOut } = useAuth();
  const { refresh } = useSubscription();
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await refresh();
    } finally {
      setRetrying(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{ bgcolor: '#FFFFFF' }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 440,
          width: '90%',
          p: 4,
          borderRadius: 3,
          textAlign: 'center',
        }}
      >
        <Box
          component="img"
          src="/easi-logo.png"
          alt="EASI"
          sx={{ width: 64, height: 64, mb: 2, display: 'inline-block' }}
        />
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1F2937', mb: 1 }}>
          Trouble loading your subscription
        </Typography>
        <Typography variant="body2" sx={{ color: '#4B5563', mb: 3, lineHeight: 1.6 }}>
          We could not reach the server. Your subscription has not been
          changed. Try again in a moment, or sign out and sign back in.
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: '#9CA3AF', mb: 3 }}>
          {error}
        </Typography>
        <Button
          variant="contained"
          fullWidth
          onClick={handleRetry}
          disabled={retrying}
          sx={{
            mb: 2,
            py: 1.25,
            background: 'linear-gradient(135deg, #2D7D46, #22C55E)',
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: 2,
            '&:hover': { background: 'linear-gradient(135deg, #256B3B, #16A34A)' },
          }}
        >
          {retrying ? 'Retrying...' : 'Retry'}
        </Button>
        <Button
          variant="text"
          onClick={handleSignOut}
          sx={{ textTransform: 'none', color: '#6B7280' }}
        >
          Sign Out
        </Button>
      </Paper>
    </Box>
  );
}

/**
 * Protected Route wrapper — shows branded loading while auth initializes
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <EasiLoadingScreen />;
  }

  if (!isAuthenticated) {
    const intended = location.pathname + location.search;
    if (intended !== '/') {
      sessionStorage.setItem('easi_redirect_after_login', intended);
    }
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * Admin Route guard — redirects non-admin users to caseload.
 * Admin role is determined from Cognito groups in the ID token.
 */
function AdminRoute({ children }: { children: React.ReactNode }) {
  try {
    const token = localStorage.getItem('easi_id_token');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const groups = payload['cognito:groups'] || [];
      const isAdmin = groups.some((g: string) => g.toLowerCase().includes('admin'));
      if (isAdmin) return <>{children}</>;
    }
  } catch {
    // Token parse failed
  }
  return <Navigate to="/" replace />;
}

/**
 * Public Route wrapper — redirects to dashboard if already authenticated
 */
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <EasiLoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/**
 * Wrapper for LanguageSampleWorkflow with route params
 */
function LanguageSamplePage() {
  const { patientId } = useParams();
  const navigate = useNavigate();

  return (
    <LanguageSampleWorkflow
      patientName="Patient"
      patientId={patientId || 'new'}
      clientAgeMonths={48}
      onComplete={() => {
        navigate(`/assessment/${patientId}`);
      }}
      onBack={() => navigate(`/assessment/${patientId}`)}
    />
  );
}

/**
 * Main App Component
 *
 * EASI 2.0 - HIPAA-Compliant Clinical Speech Assessment Platform
 */
function App() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { hasSubscription, isLoading: subLoading, subscription, error: subError } = useSubscription();
  const navigate = useNavigate();
  const hasHandledPostLoginRedirect = useRef(false);

  // Admin users bypass subscription gate
  const isAdmin = useMemo(() => {
    if (!isAuthenticated) return false;
    try {
      const token = localStorage.getItem('easi_id_token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const groups = payload['cognito:groups'] || [];
        return groups.some((g: string) => g.toLowerCase().includes('admin'));
      }
    } catch {}
    return false;
  }, [isAuthenticated]);

  // After SSO login (Google/Apple), redirect to the originally requested page.
  // Email/password login handles this in Login.tsx directly.
  // All users (including admins) land on the caseload. Admins navigate to /admin manually.
  useEffect(() => {
    if (isAuthenticated && !hasHandledPostLoginRedirect.current) {
      hasHandledPostLoginRedirect.current = true;

      // Check both sessionStorage (set by ProtectedRoute) and localStorage
      // (set by session timeout / auth failure before hard redirect)
      const redirectPath = sessionStorage.getItem('easi_redirect_after_login')
        || localStorage.getItem('easi_redirect_after_login');
      if (redirectPath) {
        sessionStorage.removeItem('easi_redirect_after_login');
        localStorage.removeItem('easi_redirect_after_login');
        navigate(redirectPath, { replace: true });
      }
      // No admin redirect — everyone lands on caseload
    } else if (!isAuthenticated) {
      hasHandledPostLoginRedirect.current = false;
    }
  }, [isAuthenticated, navigate, location.pathname]);

  // ── Subscription paywall ──────────────────────────────────────────────
  // EASI has NO free tier. All access requires an active NSS subscription.
  // This gate blocks ALL routes — no URL navigation can bypass it.
  //
  // Three render decisions, in order:
  //   1. Subscription unknown + no error -> branded loading screen.
  //   2. Subscription unknown + transient error -> recoverable error UI
  //      (NOT the paywall — we have not confirmed lack of subscription).
  //   3. Subscription known and confirmed-absent -> full-screen paywall.
  // A subscription that is known and present falls through to the app shell.
  //
  // The paywall renders ONLY on a positive no-subscription signal
  // (subscription !== null && hasSubscription === false). Any non-404 fetch
  // failure preserves the last-known-good subscription in
  // SubscriptionContext, so a transient API blip never falsely accuses a
  // paid user of having no subscription.
  if (isAuthenticated && location.pathname !== '/login') {
    if (subscription === null && subLoading && !subError) {
      return <EasiLoadingScreen />;
    }
    if (subscription === null && subError) {
      return <SubscriptionUnavailable error={subError} />;
    }
    if (subscription !== null && !hasSubscription && !isAdmin) {
      return <SubscriptionPaywall />;
    }
  }

  return (
    <React.Suspense fallback={<EasiLoadingScreen />}>
      {isAuthenticated &&
        location.pathname !== '/login' &&
        location.pathname !== '/terms-of-service' &&
        location.pathname !== '/baa-selection' &&
        location.pathname !== '/baa' &&
        <BottomDock />}
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <PageWrapper>
                  <Login />
                </PageWrapper>
              </PublicRoute>
            }
          />

          {/* Magic-link activation — public, token-based. No auth guard:
              a logged-in user following a link from their welcome email
              still needs to be able to activate. */}
          <Route
            path="/activate"
            element={
              <PageWrapper>
                <ActivateAccount />
              </PageWrapper>
            }
          />
          {/* User-initiated password reset — public, token-based. Commit A. */}
          <Route
            path="/reset-password"
            element={
              <PageWrapper>
                <ResetPassword />
              </PageWrapper>
            }
          />
          {/* Agreement routes — protected but NOT agreement-guarded */}
          <Route
            path="/terms-of-service"
            element={
              <ProtectedRoute>
                <PageWrapper>
                  <TermsOfServicePage />
                </PageWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/baa-selection"
            element={
              <ProtectedRoute>
                <PageWrapper>
                  <BAASelectionPage />
                </PageWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/baa"
            element={
              <ProtectedRoute>
                <PageWrapper>
                  <BAAAcceptancePage />
                </PageWrapper>
              </ProtectedRoute>
            }
          />

          {/* Protected routes — wrapped with AgreementGuard */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <EASISystem />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessment/:patientId"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <Assessment />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessment/:patientId/language-sample"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <LanguageSamplePage />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/subscription"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <SubscriptionPage />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />

          <Route
            path="/report-generator"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <ReportDashboard />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/report-generator/new"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <ReportGeneratorWizard />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/report-generator/:patientId"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <ReportGeneratorWizard />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/*"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AgreementGuard>
                    <PageWrapper>
                      <AdminDashboard />
                    </PageWrapper>
                  </AgreementGuard>
                </AdminRoute>
              </ProtectedRoute>
            }
          />

          {/* Literacy routes (explore mode — no patient required) */}
          <Route
            path="/literacy"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <LiteracyExploreCurriculumRoute />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/literacy/lesson/:lessonId"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <LiteracyExploreLessonRoute />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />

          {/* Literacy routes (patient mode — progress tracked) */}
          <Route
            path="/patients/:patientId/literacy"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <LiteracyDashboardRoute />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/:patientId/literacy/curriculum"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <LiteracyCurriculumRoute />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/:patientId/literacy/progress"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <LiteracyProgressRoute />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/:patientId/literacy/lesson/:lessonId"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <LiteracyLessonRoute />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />

          {/* Therapy Notes routes */}
          <Route
            path="/therapy/inbox"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <TherapyInboxRoute />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/therapy/:patientId"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <TherapySessionRoute />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
    </React.Suspense>
  );
}

export default App;
